import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Text,
  Toast,
  ToastTitle,
  tokens,
  useToastController,
} from "@fluentui/react-components";
import {
  Archive20Regular,
  ChevronDown20Regular,
  Delete20Regular,
} from "@fluentui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { archiveDocument, removeDocument } from "@/api/documents";
import { ApiError } from "@/lib/apiClient";
import type { ApprovalStatus } from "@/types/domain";

interface ApprovalActionsProps {
  documentId: string;
  approvalStatus: string;
  toasterId: string;
  archiveOnly?: boolean;
}

const STATE_BADGE: Record<
  ApprovalStatus,
  { color: "warning" | "success" | "danger" | "informative"; label: string }
> = {
  pending_review: { color: "warning", label: "Pending review" },
  approved: { color: "success", label: "Approved" },
  // Archived/removed are operator decisions, not failures — keep them
  // visually distinct from "Approved" without using the danger colour
  // (which is reserved for processing failures and is what made
  // archived docs look like errors before).
  archived: { color: "informative", label: "Archived" },
  removed: { color: "informative", label: "Removed" },
};

function badgeFor(status: string) {
  return STATE_BADGE[status as ApprovalStatus] ?? {
    color: "informative" as const,
    label: status,
  };
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.body && typeof err.body === "object" && "detail" in err.body) {
      const detail = (err.body as Record<string, unknown>).detail;
      if (typeof detail === "string") return detail;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Request failed";
}

export function ApprovalActions({
  documentId,
  approvalStatus,
  toasterId,
  archiveOnly,
}: ApprovalActionsProps) {
  const qc = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const isApproved = approvalStatus === "approved";
  const showDiscard = !isApproved && !archiveOnly;

  const archiveMutation = useMutation({
    mutationFn: () => archiveDocument(documentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document", documentId] });
      void qc.invalidateQueries({ queryKey: ["emails"] });
      void qc.invalidateQueries({ queryKey: ["email"] });
      dispatchToast(
        <Toast>
          <ToastTitle>Document archived</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
    },
    onError: (err) => {
      dispatchToast(
        <Toast>
          <ToastTitle>Archive failed: {describeError(err)}</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => removeDocument(documentId),
    onSuccess: () => {
      setConfirmRemove(false);
      void qc.invalidateQueries({ queryKey: ["document", documentId] });
      void qc.invalidateQueries({ queryKey: ["emails"] });
      void qc.invalidateQueries({ queryKey: ["email"] });
      dispatchToast(
        <Toast>
          <ToastTitle>Document removed from SharePoint</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
    },
    onError: (err) => {
      dispatchToast(
        <Toast>
          <ToastTitle>Remove failed: {describeError(err)}</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    },
  });

  const pending = archiveMutation.isPending || removeMutation.isPending;
  const badge = badgeFor(approvalStatus);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground1,
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Badge appearance="filled" color={badge.color}>
        {badge.label}
      </Badge>
      {archiveOnly ? (
        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
          Archive-only document — push not applicable.
        </Text>
      ) : null}
      <div style={{ flex: 1 }} />
      {showDiscard ? (
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <Button
              icon={
                pending ? <Spinner size="tiny" /> : <ChevronDown20Regular />
              }
              iconPosition="after"
              disabled={pending}
            >
              Discard
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={<Archive20Regular />}
                onClick={() => archiveMutation.mutate()}
              >
                Archive in SharePoint
              </MenuItem>
              <MenuItem
                icon={<Delete20Regular />}
                onClick={() => setConfirmRemove(true)}
              >
                Remove from SharePoint
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      ) : null}

      <Dialog
        open={confirmRemove}
        onOpenChange={(_, d) => {
          if (!d.open) setConfirmRemove(false);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Remove document from SharePoint?</DialogTitle>
            <DialogContent>
              This permanently deletes the SharePoint folder and the
              tracking-list row. Postgres state will record the removal for
              audit. This cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmRemove(false)}>Cancel</Button>
              <Button
                appearance="primary"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                {removeMutation.isPending ? "Removing…" : "Remove"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
